import { bakeryScreen } from './BakeryScene';
import {
  boardScreen,
  counterScreen,
  kioskScreen,
  platformScreen,
  travelCentreScreen,
} from './StationScene';
import {
  BAKERY_COUNTER,
  BAKERY_SCENARIO,
  HAUPTBAHNHOF_SCENARIO,
  HAUPTBAHNHOF_TRAVEL_CENTRE,
} from './scenes';

/**
 * Every place this build can draw. A scenario is a sequence of scenes and draws a different one of
 * these in each of them, so a screen is found by the scene it names rather than by the scenario it
 * happens to be walked in.
 *
 * A screen says two things about where what it draws is kept, because the two are not the same
 * question: `payload` is the field of the scene envelope carrying its round, and `sheet` picks the
 * account the run came to out of the run and the round it is standing in. A scenario scored on one
 * scene keeps that account inside the round of the scene that settled it; one scored on several
 * keeps it on the run, because the scene the student is standing in when it ends is not the one
 * that scored it. Neither is moved to match the other — that would make one of them lie about whose
 * account it is — so a screen says which of them is its own, and is handed both to read it from.
 */
const SCREENS = [
  bakeryScreen,
  travelCentreScreen,
  counterScreen,
  boardScreen,
  kioskScreen,
  platformScreen,
];

/** The screen that draws [sceneKey], or null when this build draws nothing for it. */
const sceneScreen = (sceneKey) => SCREENS.find((it) => it.sceneKey === sceneKey) ?? null;

/**
 * Every scenario this build can play, in the order Play offers them, each under the screen of the
 * scene it opens on — which is what names it in the list.
 *
 * Nothing here says what language a scenario is played in, nor which of them a student is
 * learning. What an entry does carry beside its screen is what holds for the whole of a run of it,
 * whichever scene the run is standing in.
 */
export const SCENARIOS = [
  { key: BAKERY_SCENARIO, screen: sceneScreen(BAKERY_COUNTER) },
  // A station has a public address and speaks over the traveller's head; a bakery has nobody
  // making announcements, and there is nothing for a run of one to listen to.
  { key: HAUPTBAHNHOF_SCENARIO, screen: sceneScreen(HAUPTBAHNHOF_TRAVEL_CENTRE), announced: true },
];

/** What this build plays under [scenarioKey], or null when it plays nothing under it. */
export const playedScenario = (scenarioKey) =>
  SCENARIOS.find((it) => it.key === scenarioKey) ?? null;

/** Whether it plays anything under it at all, which is known before a run is asked for. */
export const isPlayed = (scenarioKey) => playedScenario(scenarioKey) !== null;

/** Whether anything is ever said over a run of it, which is what opens the session to hear it. */
export const isAnnounced = (scenarioKey) => Boolean(playedScenario(scenarioKey)?.announced);

/**
 * What draws [scene] and what it is drawn from: the screen the scene names, and the round that
 * scene holds for it. Null when this build draws nothing for it.
 *
 * A scene names which of them it is and carries one field per scene, filled in for its own. Which
 * field that is belongs to the screen, so this is the one place either is read.
 */
export const drawing = (scene) => {
  const screen = sceneScreen(scene.key);
  const round = screen && scene[screen.payload];
  return round ? { screen, round } : null;
};
